import uuid from 'uuid/v4';
import axios from 'axios';
import moment from 'moment';
import 'moment/locale/da';
import { createAction } from 'redux-actions';
import isEmpty from 'lodash/isEmpty';
import { stringify } from 'qs';
import * as apiTypes from '../../constants/Api';
import { userDataUpdateLoaderAction } from '../../actions/UserData';
import { appLoadingAction } from '../../actions/App';
import { carServiceV2 } from './carV2';
import { travelService } from './travel';
import { householdService } from './household';
import { pregnancyService } from './pregnancy';
import { requestCredentials } from './auth';
import { replaceAll, resolvePath } from '../common';
import { depositService } from './deposit';
import { callService } from './service';
import { stepHasRequiredData } from '../form/formUtils';
import { nextStepAction } from '../../actions/Section';
/**
 * Switches keys around based on the Dawa documentation.
 * @param  {Object} params Params to process.
 * @param  {Object} map    Map of param keys to switch.
 * @return {Object}        New params with correct keys.
 */
export const formatParams = (params, map) => {
  const newParams = params;
  map.forEach(keyMap => {
    if (Object.hasOwnProperty.call(newParams, keyMap.name)) {
      Object.defineProperty(
        newParams,
        keyMap.mappedName,
        Object.getOwnPropertyDescriptor(newParams, keyMap.name)
      );
      delete newParams[keyMap.name];
    }
  });
  return newParams;
};

/**
 * Build a URL with an optional query string.
 * @param  {string} endpoint    The base URL.
 * @param  {Object} [params={}] Object containing the params for the query string.
 * @return {string}             The generated URL.
 */
export const buildUrl = (endpoint, params = {}) => {
  const esc = encodeURIComponent;

  if (!isEmpty(params)) {
    const query = Object.keys(params)
      .map(k => `${esc(k)}=${esc(params[k])}`)
      .join('&');
    return `${endpoint}?${query}`;
  }
  return `${endpoint}`;
};

export const callApi = (
  method, url, params, dispatch, ctForm = false,
  token = null, headers = null
) => {
  const axiosConfig = {
    url,
    method,
    timeout: 300000,
  };

  // TODO: Set authentication header with Oauth.
  switch (method) {
    case 'get':
      axiosConfig.params = params;
      break;
    case 'patch':
    case 'post':
    case 'put':
      // If this data should be sent with content type application/x-www-form-urlencoded.
      if (ctForm) {
        axiosConfig.data = stringify(params);
      } else {
        axiosConfig.data = params;
      }
      break;
    default:
  }

  if (token && typeof token === 'object') {
    axiosConfig.headers = { Authorization: `Bearer ${token.accessToken}`, id_token: token.idToken };
  } else if (token) {
    // Add token if needed.
    axiosConfig.headers = { Authorization: token };
  }

  axiosConfig.headers = {
    ...axiosConfig.headers,
    ...headers,
  };

  return axios(axiosConfig);
};

const callApiWithoutToken = (method, url, params, dispatch,
                             ctForm = false, headers = null) => {
  const newParms = { ...params };
  delete newParms.responseAsIs;

  return callApi(method, url, newParms, dispatch, ctForm, headers)
    .then(data => {
      if (params.responseAsIs) {
        return data.data;
      }
      return {
        ...data.data,
        usedParams: params,
      };
    });
};

const callApiWithToken = (method, url, params, dispatch,
                          ctForm = false, headers = null, consents = false) => {
  let newParms = { ...params };
  if (params instanceof Array) {
    newParms = params;
  }
  delete newParms.responseAsIs;

  return dispatch(requestCredentials(consents))
    .then(token => callApi(method, url, newParms, dispatch, ctForm, token, headers)
      .then(data => {
        if (params && params.responseAsIs) {
          return data.data;
        }
        return {
          ...data.data,
          usedParams: params,
        };
      }));
};

/**
 * Call a URL using GET. Params will be added as query string.
 * @param  {string} url    The enpoint.
 * @param  {object} params Params to add as query string.
 * @return {Promise}        The promise.
 */
export const callApiGet = (url, params = null, dispatch = null, useToken = true,
                           headers = null, consents = false) => {
  if (useToken) {
    return callApiWithToken('get', url, params, dispatch, false, headers, consents);
  }
  return callApiWithoutToken('get', url, params, dispatch, headers);
};

/**
 * Call a URL using POST. Data will be added as request data.
 * @param  {string} url    The enpoint.
 * @param  {object} data   Params to add as data.
 * @return {Promise}       The promise.
 */
export const callApiPost = (
  url,
  data,
  dispatch,
  ctForm = false,
  useToken = true,
  headers = null,
  consents = false
) => {
  if (useToken) {
    return callApiWithToken('post', url, data, dispatch, ctForm, headers, consents);
  }
  return callApiWithoutToken('post', url, data, dispatch, ctForm, headers);
};

/**
 * Call a URL using PATCH. Data will be added as request data.
 * @param  {string} url    The enpoint.
 * @param  {object} data   Params to add as data.
 * @return {Promise}       The promise.
 */
export const callApiPatch = (
  url,
  data,
  dispatch,
  useToken = true,
  headers = null,
  consents = false) => {
  if (useToken) {
    return callApiWithToken('patch', url, data, dispatch, false, headers, consents);
  }
  return callApiWithoutToken('patch', url, data, dispatch, headers);
};

/**
 * Call a URL using PUT. Data will be added as request data.
 * @param  {string} url    The enpoint.
 * @param  {object} data   Params to add as data.
 * @return {Promise}       The promise.
 */
export const callApiPut = (
  url,
  data,
  dispatch,
  useToken = true,
  headers = null,
  consents = false) => {
  if (useToken) {
    return callApiWithToken('put', url, data, dispatch, false, headers, consents);
  }
  return callApiWithoutToken('put', url, data, dispatch, headers);
};

/**
 * Contructs the correct API url.
 * @param {string} type             The type of service to call.
 * @param {string} service          The service to use.
 * @param {string} serviceEndPoint  Service path.
 * @return {string}                 The url.
 */
export const constructApiUrl = (host, service) => {
  return `${host || apiTypes.API_ENDPOINT}/${service}`;
};

/**
 * Contructs the correct API url.
 * @param {string} type             The type of service to call.
 * @param {string} service          The service to use.
 * @param {string} serviceEndPoint  Service path.
 * @return {string}                 The url.
 */
export const constructCustomerApiUrl = (type, service, serviceEndPoint) => {
  const serviceUrl = serviceEndPoint || 'calcbuy';
  if (!type) {
    return `${apiTypes.API_ENDPOINT}/${serviceUrl}/${service}`;
  }
  if (!service) {
    return `${apiTypes.API_ENDPOINT}/${serviceUrl}/${type}`;
  }
  return `${apiTypes.API_ENDPOINT}/${serviceUrl}/${type}/${service}`;
};

/**
 * Call the API using POST.
 * @param  {string} service The service to call.
 * @param  {Object} params  The params for the service.
 * @return {Promise}        The Promise.
 */
export const callCustomerApiPost = (service, params, dispatch) => {
  const type = 'customer';
  const url = constructCustomerApiUrl(type, service);
  return callApiPost(url, params, dispatch);
};

/**
 * Function that maps the params needed with their values.
 * @param  {array}  params     Array of param fields needed in the service. Defined in the JSON.
 * @param  {Object} formValues The current form values.
 * @param  {Object} state      The current state
 * @param  {string} product    If we need to fetch product specific values.
 * @return {Object}            Object containing the needed params for the service.
 */
export const createParams = (params, formValues, state, product = null, extraData = {}) => {
  // Merge state and formValues. Form can override state.
  let values = {
    ...state.userData,
    ...formValues,
    ...extraData,
  };

  if (product && state.userData[product]) {
    values = {
      ...values,
      ...state.userData[product],
    };
  }
  // Now map the values to their respective keys.
  const mappedParams = params.reduce((result, item) => {
    const newResult = result;
    let key = item.valueFrom ? item.valueFrom : item.field;
    key = replaceAll(key, state.section.packageData.translations, values);
    if (Object.hasOwnProperty.call(item, 'value')) {
      newResult[item.field] = item.value;
    } else if (Object.hasOwnProperty.call(values, key)) {
      if (values[key] === false || values[key] || values[key] === '') {
        newResult[item.field] = values[key];
      }
    } else {
      const value = resolvePath(values, key);
      if (value !== undefined) {
        newResult[item.field] = value;
      }
    }
    return newResult;
  }, {});
  return mappedParams;
};

/**
 * Function that ensures that parameters are set as defined in the service.
 * @param  {Object} mappedParams  The parameter object to validate.
 * @param  {array}  serviceParams The params defined in the JSON config.
 * @return {array}                Array of errors found.
 */
export const validateParams = (mappedParams, serviceParams) => {
  const errors = serviceParams.reduce((result, param) => {
    let curErrors = result;
    if (param.required &&
      (!Object.hasOwnProperty.call(mappedParams, param.field))) {
      curErrors = [...result, param.field];
    }
    return curErrors;
  }, []);
  return errors;
};

/**
 * Function that return an object containing params that are either not in the prevParams or that
 * are updated.
 * @param  {Object} curParams         The params being used for current request.
 * @param  {Object} prevParams        Params used at a previous request.
 * @param  {Array}  persist           Array of fields to persist in the params.
 * @param  {Object} additionalParams  Array of fields to persist in the params.
 * @return {Object}                   The udpated params.
 */
export const getChangedParams = (curParams, prevParams, persist = [], additionalParams = {}) => {
  const returnParams = Object.keys(curParams).reduce((result, key) => {
    const curResult = result;
    // If we should persist this param just return it.
    // If no params has been set earlier we use it.
    // If the two params are different we should update the params.
    if (prevParams && (!Object.hasOwnProperty.call(prevParams, key) ||
      persist.includes(key) ||
      curParams[key] !== prevParams[key]
    )) {
      curResult[key] = curParams[key];
      return curResult;
    }

    return curResult;
  }, {});
  return {
    ...returnParams,
    ...additionalParams,
  };
};

export const portraitLead = createAction(apiTypes.PORTRAIT_LEAD);

/**
 * Dispatches the portrait lead action.
 * @param  {string} service The service to use.
 * @param  {Object} params The params to call the service with.
 * @return {function} Dispatch function.
 */
const callPortraitLead = (service, params) => {
  return dispatch => {
    dispatch(portraitLead());
    return callCustomerApiPost(service, params, dispatch);
  };
};

/**
 * Call the portrait lead service.
 * @return {Promise}           The promise of the service.
 */
export const requestPortraitLead = (params, formValues) => {
  return (dispatch, getState) => {
    const paramsForService = {
      ...createParams(params, formValues, getState(), ''),
    };
    // Condition to differentiate private and commercial lead controller call
    const service = (formValues.setRecipient === 'Erhverv') ? 'generateCommercialLead' : 'generateLead';
    return dispatch(callPortraitLead(service, paramsForService));
  };
};

export const portraitLeadCreated = createAction(apiTypes.PORTRAIT_LEAD_CREATED);
export const portraitLeadCreatedAction = payload => dispatch =>
  dispatch(portraitLeadCreated(payload));

export const portratiLeadAction = (params, formValues) => (dispatch) =>
  dispatch(requestPortraitLead(params, formValues)).then((data) => {
    return dispatch(portraitLeadCreatedAction({ ...data }));
  }).catch(data => {
    return Promise.resolve(data);
  });

export const activationDateBaseModel = (formValues) => {
  const { basketList, forProduct } = formValues;
  const product = forProduct || basketList[0];
  switch (product) {
    case 'car':
      return carServiceV2.getCarMapping(formValues);
    case 'travel':
      return travelService.getTravelMapping(formValues);
    case 'household':
      return householdService.getHouseholdMapping(formValues);
    case 'pregnancy':
      return pregnancyService.getPregnancyMapping(formValues);
    default:
      return Promise.resolve();
  }
};
export const callPurchaseService = (params, formValues, dispatch, actionOnErrors) => {
  const { basketList, forProduct } = formValues;
  const product = forProduct || basketList[0];
  switch (product) {
    case 'car':
      return dispatch(carServiceV2.purchase(params, formValues, actionOnErrors));
    case 'travel':
      return dispatch(travelService.purchase(params, formValues, actionOnErrors));
    case 'deposit':
      return dispatch(depositService.purchase(params, formValues, actionOnErrors));
    case 'pregnancy':
      return dispatch(pregnancyService.purchase(params, formValues, actionOnErrors));
    default:
      return Promise.resolve();
  }
};


export const callCreateCustomerService = (params, formValues, dispatch, actionOnErrors) => {
  const { basketList, forProduct } = formValues;
  const product = forProduct || basketList[0];
  switch (product) {
    case 'car':
    case 'travel':
      return dispatch(carServiceV2.createCustomer(params, formValues, actionOnErrors));
    case 'deposit':
      return dispatch(depositService.createCustomer(params, formValues, actionOnErrors));
    default:
      return Promise.resolve();
  }
};

export const consentServiceProduct = (saveConsentService, formValues, dispatch) => {
  const constantService = saveConsentService.filter(data => data.serviceName === 'consentsService');
  const gpcService = saveConsentService.filter(data => data.serviceName === 'consentGPCService');
  let finishedService = 0;

  constantService.forEach((item) => {
    callService(
      item.serviceName,
      item.serviceParams,
      item.forProduct,
      formValues,
      dispatch
    ).then(() => {
      finishedService++;
      if (finishedService === constantService.length) {
        callService(
          gpcService[0].serviceName,
          gpcService[0].serviceParams,
          gpcService[0].forProduct,
          formValues,
          dispatch
        ).then((data) => {
          return data;
        });
      }
    });
  });
};

export const doUpdateLoader = (data) => {
  const message = (data === undefined) ? '' : data.message;
  return message !== 'actionOnError' && message !== 'appErrorAction';
};

function servicesToCallForSequence(allServicesToCall, totalNumberOfServices, formValues, dispatch, updateLoader, serviceProps) {
  const { lastService, useSpinner, saveConsentService, generateLeadService } = serviceProps;
  if (allServicesToCall.length > 0) {
    const servicesToCall = allServicesToCall.filter(service => {
      return (service.sequenceNumber === allServicesToCall[0].sequenceNumber);
    });
    allServicesToCall = allServicesToCall.filter((service) => !servicesToCall.includes(service));

    let finishedServicesInSequence = 0;
    servicesToCall.forEach((service) => {
      const {
        serviceName, serviceParams, forProduct, actionOnErrors, requiredData
      } = service;
      let isValid = true;
      if (requiredData) {
        isValid = stepHasRequiredData(requiredData, formValues, formValues);
      }
      if (isValid) {
        callService(
          serviceName,
          serviceParams,
          forProduct,
          formValues,
          dispatch,
          actionOnErrors
        ).then((data) => {
          finishedServicesInSequence++;
          if (finishedServicesInSequence === servicesToCall.length) {
            // Call recursively to handle next sequenceNumber
            servicesToCallForSequence(
              allServicesToCall,
              totalNumberOfServices,
              formValues,
              dispatch,
              updateLoader,
              serviceProps
            )
          }
          if (updateLoader) {
            if (doUpdateLoader(data)) {
              dispatch(userDataUpdateLoaderAction(1 / totalNumberOfServices));
            }
          }
        });
      } else {
        finishedServicesInSequence++;
        if (finishedServicesInSequence === servicesToCall.length) {
          // Call recursively to handle next sequenceNumber
          servicesToCallForSequence(
            allServicesToCall,
            totalNumberOfServices,
            formValues,
            dispatch,
            updateLoader,
            serviceProps
          )
        }
        if (updateLoader) {
          dispatch(userDataUpdateLoaderAction(1 / totalNumberOfServices));
        }
      }
    });
  } else {
    if (lastService) {
      callService(
        lastService.serviceName,
        lastService.serviceParams,
        lastService.forProduct,
        formValues,
        dispatch
      ).then(() => {
        if (updateLoader) {
          dispatch(userDataUpdateLoaderAction(1));
        }
        // Stop spinner if needed.
        if (useSpinner) {
          dispatch(appLoadingAction(false));
        }
      }).finally(() => {
        if (generateLeadService) {
          dispatch(portratiLeadAction(generateLeadService.serviceParams, formValues));
        }
        consentServiceProduct(saveConsentService, formValues, dispatch);
      });
    }
    if (useSpinner) {
      // Stop spinner if needed.
      dispatch(appLoadingAction(false));
    }
  }
}

/**
 * Calls an array of services. If this is a chain for the loader we update that as well.
 * @param  {array}    services     The services array.
 * @param  {Object}   serviceProps Props to use for this service.
 * @param  {Object}   updateLoader Reset loader.
 * @param  {Object}   formValues   The current values.
 * @param  {Function} dispatch     The dispatch function.
 */
export const callServices = (
  services,
  updateLoader,
  serviceProps,
  formValues,
  dispatch
) => {
  // First we need to filter out services we should not call and sort by sequenceNumber.
  const servicesToCall = services.filter(service => {
    return !(service.forProduct && !formValues.basketList.includes(service.forProduct));
  }).sort((a, b) => {
    return (a.sequenceNumber || 0) - (b.sequenceNumber || 0)
  });
  const { useSpinner } = serviceProps;
  // Start spinner if needed.
  if (useSpinner) {
    dispatch(appLoadingAction(true));
  }
  servicesToCallForSequence(
    servicesToCall,
    servicesToCall.length,
    formValues,
    dispatch,
    updateLoader,
    serviceProps
  );
};

/**
 * Object Mapping.
 * @param  {Object} targetObj target object
 * @param  {Object} sourceObj  source object
 * * @return {Object}
 */
export const objectMapping = (targetObj, sourceObj) => {
  const resultObj = targetObj;
  Object.keys(sourceObj).forEach((key) => {
    resultObj[key] = sourceObj[key];
  });
  return resultObj;
};
/**
 * Get value based on Environment.
 * @param env
 * @param data
 * @return {*}
 */
export const getEnvValue = (env, data) => {
  if (typeof data === 'object') {
    return data[env] ? data[env] : data.default;
  }
  return data;
};

export const fetchWidget = (host = '', tenant, path) => {
  return callApiGet(`${host}/car/calcnbuy/${tenant}/${path}`, { responseAsIs: true }, null, false, false);
};

export const encodeState = (params, host = '') => {
  return callApiGet(`${host}/service/state`, { params, responseAsIs: true }, null, false, false);
};

export const decodeState = (params, host = '') => {
  return callApiGet(`${host}/service/token`, { params, responseAsIs: true }, null, false, false);
};

export const getValueFromObjects = (field, ...objects) => {
  return objects ? objects.reduce((value, obj) => {
    return value || obj[field];
  }, null) : null;
};
export const calculateAge = (date) => {
  const birthDate = moment(date, 'DD-MM-YYYY');
  return moment(new Date()).diff(birthDate, 'years');
};
export const getCustomerAge = (mappedParams, formValues) => {
  let age = getValueFromObjects('age', mappedParams, formValues);
  const birthDate = getValueFromObjects('Birthdate', mappedParams, formValues);
  if (age == null && birthDate !== null) {
    age = calculateAge(birthDate);
  }
  return age;
};
export const getTravelValue = (packageText, requestModal) => {
  let { travelValue } = requestModal.travel;
  if ((packageText === 'Super' || packageText === 'Udvidet') && travelValue === '0') {
    travelValue = '';
  }
  return travelValue;
};
export const getAddressMapping = (mappedParams, formValues) => {
  return {
    Street: getValueFromObjects('street', mappedParams, formValues),
    HouseNumber: getValueFromObjects('houseNumber', mappedParams, formValues),
    ToHouseNumber: getValueFromObjects('toHouseNumber', mappedParams, formValues),
    FloorNumber: getValueFromObjects('floorNumber', mappedParams, formValues),
    UnitNumber: getValueFromObjects('unitNumber', mappedParams, formValues),
    City: getValueFromObjects('city', mappedParams, formValues),
    PostalCode: getValueFromObjects('postalCode', mappedParams, formValues),
    Country: getValueFromObjects('country', mappedParams, formValues),
    CountryCode: getValueFromObjects('countryCode', mappedParams, formValues),
    KVHX: getValueFromObjects('KVHX', mappedParams, formValues),
  };
};

export const getAddressMappingV2 = (mappedParams, formValues) => {
  return {
    street: getValueFromObjects('street', mappedParams, formValues),
    houseNumber: getValueFromObjects('houseNumber', mappedParams, formValues),
    toHouseNumber: getValueFromObjects('toHouseNumber', mappedParams, formValues),
    floorNumber: getValueFromObjects('floorNumber', mappedParams, formValues),
    unitNumber: getValueFromObjects('unitNumber', mappedParams, formValues),
    city: getValueFromObjects('city', mappedParams, formValues),
    postalCode: getValueFromObjects('postalCode', mappedParams, formValues),
    country: getValueFromObjects('country', mappedParams, formValues),
    countryCode: getValueFromObjects('countryCode', mappedParams, formValues),
    KVHX: getValueFromObjects('KVHX', mappedParams, formValues),
  };
};

export const getCPRMapping = (mappedParams, formValues) => {
  return (
    getValueFromObjects('civilRegistrationNumber', mappedParams, formValues) || ''
  ).replace('-', '');
};

export const calculatePriceAndFee = (price, feeList, formValues) => {
  // const priceWithOutFee = Number(price.premiumWithOutFee);
  const priceWithFee = Math.round(price.premiumAmount * 100) / 100;

  return {
    premiumPrice: price.premiumWithOutFee,
    priceWithFee: `${priceWithFee}`,
    insuranceFee: price.insuranceToll,
    paymentFee: formValues.paymentFee || '',
  };
};

export const prepareCommonCreateUpdateResponse = (payload, formValues) => {
  const response = {
    sessionKey: payload.generalInsurance.sessionKey,
    deductible: payload.deductible,
    paymentFrequency: payload.payment.paymentFrequency,
    paymentMethod: payload.payment.paymentMethod,
    priceApiResponse: [
      {
        offeredPrice: calculatePriceAndFee(
          payload.price.offeredPrice,
          payload.price.fees,
          formValues
        ),
        listPrice: calculatePriceAndFee(
          payload.price.listPrice,
          payload.price.fees,
          formValues
        ),
        advantagePrice: calculatePriceAndFee(
          payload.price.advantagePrice,
          payload.price.fees,
          formValues
        ),
        discountAmount:
          calculatePriceAndFee(
            payload.price.listPrice,
            payload.price.fees,
            formValues
          ).priceWithFee -
          calculatePriceAndFee(
            payload.price.offeredPrice,
            payload.price.fees,
            formValues
          ).priceWithFee,
      },
    ],
    totalOfferedPrice: {
      priceWithFee: payload.price.offeredPrice.premiumWithOutFee,
    },
    usedParams: payload.usedParams,
  };

  const listPrice = response.priceApiResponse[0].listPrice.premiumPrice;
  const offeredPrice = response.priceApiResponse[0].offeredPrice.premiumPrice;
  const discount = Math.round(((listPrice - offeredPrice) / listPrice) * 100);

  response.discount = `${discount < 20 ? 20 : discount}`;
  return response;
};

export const getParameterCaseInsensitive = (object, key) => {
  return object[Object.keys(object)
    .find(k => k.toLowerCase() === key.toLowerCase())
    ];
};

/**
 * This method takes keyValueMap json Array of objects like shown below:
 * keyValueMapList = [{ 'findWith': 'A', 'pairWith': 'B', 'defaultValue': 'C' },
 *                    { 'findWith': 'X', 'pairWith': 'Y', 'defaultValue': 'Z' }];
 *
 * The function tries to finds the value from input params {i.e. "mappedParams" && "formValues"}
 * by calling "getValueFromObjects" function and it puts the found value in the return object and
 * if it is unable to find the value from the "getValueFromObjects" function then it sets the
 * "defaultValue" provided as a 3rd property in the JSON objects present
 * in *** {keyValueMapList} ***
 * @param {Array} keyValueMapList
 * @param {Object} mappedParams
 * @param {Object} formValues
 * @returns {Object} mapped values in a form of JSON Array
 */
export const getMapping = (keyValueMapList, mappedParams, formValues) => {
  const mappedObject = {};
  if (keyValueMapList) {
    keyValueMapList.forEach((keyValuePair) => {
      mappedObject[keyValuePair.pairWith] =
        getValueFromObjects(keyValuePair.findWith, mappedParams, formValues) ||
        keyValuePair.defaultValue;
    });
  }
  return mappedObject;
};

export const getLeadDescription = (mappedParams, formValues) => {
  return {
    campaignDescription: getValueFromObjects('CampaignDescription', mappedParams, formValues),
    subject: getValueFromObjects('subject', mappedParams, formValues) || '',
    comment: getValueFromObjects('comment', mappedParams, formValues),
    message: getValueFromObjects('message', mappedParams, formValues),
    information: getValueFromObjects('information', mappedParams, formValues) || '',
    additionalInfo: getValueFromObjects('additionalInfo', mappedParams, formValues),
    recommendation: getValueFromObjects('recommendation', mappedParams, formValues) || '',
    misc: getValueFromObjects('misc', mappedParams, formValues) || '',
  };
};

export const getLeadProductDetails = (mappedParams, formValues) => {
  return {
    productName: getValueFromObjects('produktnavn', mappedParams, formValues) || '',
    policyNumber: getValueFromObjects('policyNumber', mappedParams, formValues) || '',
    leadPartnerName: getValueFromObjects('leadPartnerName', mappedParams, formValues) || '',
    coverage: getValueFromObjects('coverage', mappedParams, formValues) || '',
    price: getValueFromObjects('price', mappedParams, formValues) || '',
    additionalPrice: getValueFromObjects('additionalPrice', mappedParams, formValues) || '',
    changeDescription: getValueFromObjects('changeDescription', mappedParams, formValues) || '',
  };
};

export const getLeadSource = (mappedParams, formValues) => {
  return {
    url: window.location.href,
    site: getValueFromObjects('site', mappedParams, formValues) || '',
    lastViewedPage: getValueFromObjects('lastViewedPage', mappedParams, formValues) || '',
  };
};

export const getDefaultOrValueFromObjects = (
  field,
  mappedParams,
  formValues,
  defaultValue = ''
) => {
  if (
    getValueFromObjects(field, mappedParams, formValues) === undefined ||
    getValueFromObjects(field, mappedParams, formValues) === null
  ) {
    return defaultValue;
  }
  return getValueFromObjects(field, mappedParams, formValues);
};

export const getOneYearActivationDate = (data) => {
  const date = data.activationDate.split('-');
  const dateText = data.activationDateText.split(' ');
  const newActivationDate = `${date[0]}-${date[1]}-${+date[2] + 1}`;
  const newActivationDateText = `${dateText[0]} ${dateText[1]} ${+dateText[2] + 1}`;
  return {
    activationDate: newActivationDate,
    activationDateText: newActivationDateText,
    minActivationDate: data.minActivationDate,
    oneYearActivationDateText: newActivationDateText,
  };
};


export const getNext12MonthNamesWithYear = (now = new Date()) => {
  const monthsRequired = 12;
  const response = {};
  for (let i = 1; i <= monthsRequired; i++) {
    response[`${i}`] = {
      key: moment(now, 'DD-MM-YYYY').add(i, 'months').format('01-MM-YYYY'),
      value: moment(now, 'MM-YYYY').locale('da').add(i, 'months').format('MMMM YYYY'),
    };
  }
  return response;
};

export const getPetActivationDate = (data, formValues, numberOfDays) => {
  const inputDob = formValues.petDob;
  const petDob = inputDob.split('-').reverse().join('-');
  const currentDate = moment();
  const petDobWeek = moment(petDob).locale('en-gb').format('LL');
  const diffDays = currentDate.diff(petDobWeek, 'days')
  let newPetDob = '';
  let petActivationText = '';
  if (diffDays < numberOfDays) {
    const apiInputDob = moment(petDobWeek).add(numberOfDays, 'days');
    newPetDob = moment(apiInputDob).format('DD-MM-YYYY');
    petActivationText = moment(apiInputDob, 'MM-YYYY').locale('da').format('DD. MMMM YYYY');
  }
  else {
    newPetDob = data.activationDate;
    petActivationText = data.activationDateText;
  }
  return {
    activationDate: newPetDob,
    activationDateText: petActivationText,
    minActivationDate: data.minActivationDate,
    oneYearActivationDateText: newPetDob,
    newActivationDate: newPetDob,
    newActivationDateText: petActivationText,
  };
};

export const getCurrentDate = () => {
  const currentDate = moment().format('DD-MM-YYYY');

  return {
    activationDate: currentDate,
    newActivationDate: currentDate,
  };
};

export const validateBankAccountAction = createAction(apiTypes.BANK_ACCOUNT_VALID);
export const checkBankAccountDetails = (params,formValues) => {
  return async (dispatch) => {
    
    const bankRegistrationNumber = formValues?.bankRegistrationNumber;
    const bankAccountNumber = formValues?.bankAccountNumber;
    const service = '/tryg-external/dk/financialtransaction/payment';

    const bodyObj = {
      "bankAccountNumber": bankAccountNumber,
      "registrationNumber": bankRegistrationNumber
    }

    const headers = getRequestHeader();

    try {
      const data  = await callApiPost(service, bodyObj, dispatch, false, true, headers, true); 
      dispatch(validateBankAccountAction({
        validAccountNo: data?.message,
        bankRegistrationNumber: data?.usedParams?.registrationNumber,
        bankAccountNumber: data?.usedParams?.bankAccountNumber,
        Payment_service: true,
        hasValidAccountInfo: true
      }));
      let nextStep = params?.nextStep
      if(data?.message === "Valid" && typeof nextStep === "undefined") {
        dispatch(nextStepAction());
      } else if(data?.message === "Invalid" && nextStep === false) {
        throw new Error("Invalid");  
      }
    } catch(error) {
      dispatch(validateBankAccountAction({
        validAccountNo: "Invalid",
        hasValidAccountInfo: false
      }));
    }
  };
}

/**
 * This method used to return the header object for request
 * @returns
 */
export const getRequestHeader = () => {
  return {
    'Global-Transaction-ID': uuid(),
    'Enterprise-Application-ID': apiTypes.ENTERPRISE_APPLICATION_ID,
    'Application-Name': apiTypes.APPLICATION_NAME,
  };
}

/**
 * This method takes in an object
 * checks if all api calls has been successfully completed
 * @returns
 */
export const checkAllApiCallsStatus = (apiCallsStatus) => {
  return Object.values(apiCallsStatus).every(status => status === true);
}